import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { Turnstile } from "@marsidev/react-turnstile";
import { verifyTurnstile } from "../components/api/verify-turnstile";

const DesktopSecurityCheck = ({
  theme,
  children,
}) => {
  const dark = theme === "dark";

  // ========================================
  // DEVICE MODE
  // ========================================

  const [deviceMode, setDeviceMode] = useState(null);

  // ========================================
  // VERIFICATION STATE
  // ========================================

  const [verified, setVerified] = useState(false);
  const [needsVerification, setNeedsVerification] =
    useState(false);

  const [token, setToken] = useState("");
  const [verifying, setVerifying] = useState(false);

  // ========================================
  // PREVIOUS MODE
  // ========================================

  const previousModeRef = useRef(null);

  // ========================================
  // INITIAL LOAD
  // ========================================

  const initializedRef = useRef(false);

  // ========================================
  // GET RESPONSIVE MODE
  // ========================================

  const getDeviceMode = () => {
    const width = window.innerWidth;

    if (width < 768) {
      return "mobile";
    }

    if (width < 1024) {
      return "tablet";
    }

    return "desktop";
  };

  // ========================================
  // DETECT MODE CHANGE
  // ========================================

  useEffect(() => {
    const checkDeviceMode = () => {
      const currentMode = getDeviceMode();

      console.log("Security check:", {
        width: window.innerWidth,
        currentMode,
        previousMode:
          previousModeRef.current,
      });

      // ======================================
      // FIRST LOAD
      // ======================================

      if (previousModeRef.current === null) {
        previousModeRef.current = currentMode;

        setDeviceMode(currentMode);

        // First load:
        // NO verification
        setNeedsVerification(false);
        setVerified(true);

        initializedRef.current = true;

        return;
      }

      // ======================================
      // MODE CHANGED
      // ======================================

      if (
        currentMode !==
        previousModeRef.current
      ) {
        console.log(
          `Mode changed: ${previousModeRef.current} → ${currentMode}`
        );

        // Save new mode
        previousModeRef.current =
          currentMode;

        setDeviceMode(currentMode);

        // ====================================
        // REQUIRE NEW VERIFICATION
        // ====================================

        setVerified(false);
        setNeedsVerification(true);

        setToken("");
        setVerifying(false);

        return;
      }

      // ======================================
      // SAME MODE
      // ======================================

      setDeviceMode(currentMode);

      // IMPORTANT:
      // Do nothing here.
      //
      // Resizing inside the same mode
      // does NOT trigger verification.
    };

    checkDeviceMode();

    window.addEventListener(
      "resize",
      checkDeviceMode
    );

    return () => {
      window.removeEventListener(
        "resize",
        checkDeviceMode
      );
    };
  }, []);

  // ========================================
  // TURNSTILE SUCCESS
  // ========================================

  const handleVerify = async (
    turnstileToken
  ) => {
    if (!turnstileToken) {
      setToken("");
      setVerified(false);
      setNeedsVerification(true);
      return;
    }

    setToken(turnstileToken);
    setVerifying(true);

    try {
      const response =
        await verifyTurnstile(
          turnstileToken
        );

      console.log(
        "Turnstile response:",
        response
      );

      if (
        response?.data?.success === true
      ) {
        setVerified(true);
        setNeedsVerification(false);
      } else {
        setToken("");
        setVerified(false);
        setNeedsVerification(true);
      }
    } catch (error) {
      console.error(
        "Turnstile verification failed:",
        error.response?.data ||
          error.message
      );

      setToken("");
      setVerified(false);
      setNeedsVerification(true);
    } finally {
      setVerifying(false);
    }
  };

  // ========================================
  // TURNSTILE EXPIRED
  // ========================================

  const handleExpire = () => {
    console.log(
      "Turnstile expired"
    );

    setToken("");
    setVerified(false);
    setNeedsVerification(true);
    setVerifying(false);
  };

  // ========================================
  // TURNSTILE ERROR
  // ========================================

  const handleError = (error) => {
    console.error(
      "Turnstile error:",
      error
    );

    setToken("");
    setVerified(false);
    setNeedsVerification(true);
    setVerifying(false);
  };

  // ========================================
  // INITIAL RENDER
  // ========================================

  if (deviceMode === null) {
    return children;
  }

  // ========================================
  // NO VERIFICATION REQUIRED
  // ========================================

  if (!needsVerification) {
    return children;
  }

  // ========================================
  // SECURITY SCREEN
  // ========================================

  return (
    <div
      style={{
        ...styles.container,
        background: dark
          ? "#111827"
          : "#f5f5f5",
      }}
    >
      <div
        style={{
          ...styles.card,
          background: dark
            ? "#1f2937"
            : "#ffffff",
          color: dark
            ? "#ffffff"
            : "#111111",
        }}
      >
        {/* LOGO */}

        <div style={styles.logo}>
          🔒
        </div>

        {/* TITLE */}

        <h2 style={styles.title}>
          Security Verification
        </h2>

        {/* DESCRIPTION */}

        <p
          style={{
            ...styles.description,
            color: dark
              ? "#d1d5db"
              : "#666666",
          }}
        >
          Please verify that you are human
          before continuing.
        </p>

        {/* TURNSTILE */}

        <div style={styles.turnstile}>
          <Turnstile
            key={`${deviceMode}-${needsVerification}`}
            siteKey={
              process.env
                .REACT_APP_TURNSTILE_SITE_KEY
            }
            options={{
              theme: dark
                ? "dark"
                : "light",
            }}
            onSuccess={
              handleVerify
            }
            onExpire={
              handleExpire
            }
            onError={
              handleError
            }
          />
        </div>

        {/* VERIFYING */}

        {verifying && (
          <p
            style={{
              ...styles.verifying,
              color: dark
                ? "#d1d5db"
                : "#555555",
            }}
          >
            Verifying your browser...
          </p>
        )}

        {/* INSTRUCTION */}

        {!token &&
          !verifying && (
            <p
              style={{
                ...styles.smallText,
                color: dark
                  ? "#9ca3af"
                  : "#666666",
              }}
            >
              Complete the verification
              above to continue.
            </p>
          )}
      </div>
    </div>
  );
};

// ========================================
// STYLES
// ========================================

const styles = {
  container: {
    position: "fixed",
    inset: 0,

    width: "100%",
    height: "100vh",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    padding: "20px",

    boxSizing: "border-box",

    zIndex: 999999,
  },

  card: {
    width: "100%",
    maxWidth: "420px",

    borderRadius: "12px",

    padding: "35px 25px",

    textAlign: "center",

    boxShadow:
      "0 5px 30px rgba(0, 0, 0, 0.12)",

    boxSizing: "border-box",
  },

  logo: {
    fontSize: "42px",
    marginBottom: "10px",
  },

  title: {
    margin: "0 0 10px",

    fontSize: "24px",

    fontWeight: "600",
  },

  description: {
    margin: "0",

    fontSize: "15px",

    lineHeight: "1.5",
  },

  turnstile: {
    marginTop: "25px",

    display: "flex",

    justifyContent: "center",

    minHeight: "65px",
  },

  verifying: {
    marginTop: "15px",

    fontSize: "14px",
  },

  smallText: {
    fontSize: "13px",

    marginTop: "15px",
  },
};

export default DesktopSecurityCheck;